/**
 * @fileoverview Memory Game types
 * @description Type definitions for the Memory (Puzzle Matching) game module
 */

/** Card state */
export type CardState = "hidden" | "revealed" | "matched";

/** Grid size options */
export type GridSize = "3x4" | "4x4" | "4x5" | "5x6";

/** Image set type */
export type ImageSetType = "family" | "random";

/** Game difficulty based on grid size */
export interface DifficultyConfig {
  gridSize: GridSize;
  rows: number;
  cols: number;
  totalCards: number;
  pairs: number;
  label: string;
  timeBonus: number; // seconds for max time bonus
}

/** Difficulty presets */
export const DIFFICULTY_CONFIGS: Record<GridSize, DifficultyConfig> = {
  "3x4": {
    gridSize: "3x4",
    rows: 3,
    cols: 4,
    totalCards: 12,
    pairs: 6,
    label: "Makkelijk",
    timeBonus: 60,
  },
  "4x4": {
    gridSize: "4x4",
    rows: 4,
    cols: 4,
    totalCards: 16,
    pairs: 8,
    label: "Normaal",
    timeBonus: 90,
  },
  "4x5": {
    gridSize: "4x5",
    rows: 4,
    cols: 5,
    totalCards: 20,
    pairs: 10,
    label: "Moeilijk",
    timeBonus: 120,
  },
  "5x6": {
    gridSize: "5x6",
    rows: 5,
    cols: 6,
    totalCards: 30,
    pairs: 15,
    label: "Expert",
    timeBonus: 180,
  },
};

/** A single memory card */
export interface MemoryCard {
  id: string;
  pairId: string;
  imageUrl: string;
  state: CardState;
  position: number;
}

/** Game status */
export type GameStatus = "idle" | "playing" | "paused" | "completed";

/** Game state stored in game_sessions.game_state */
export interface MemoryGameState {
  cards: MemoryCard[];
  gridSize: GridSize;
  imageSetType: ImageSetType;
  status: GameStatus;
  moves: number;
  matchedPairs: number;
  totalPairs: number;
  startedAt: string | null;
  completedAt: string | null;
  revealedCards: string[]; // card IDs currently revealed (max 2)
  lastMoveAt: string | null;
}

/** Props for a memory card component */
export interface MemoryCardProps {
  card: MemoryCard;
  isRevealed: boolean;
  isMatched: boolean;
  disabled: boolean;
  onPress: () => void;
  cardSize: number;
}

/** Props for the memory board component */
export interface MemoryBoardProps {
  cards: MemoryCard[];
  revealedCards: string[];
  disabled: boolean;
  gridSize: GridSize;
  onCardPress: (cardId: string) => void;
}

/** Game result for stats */
export interface MemoryGameResult {
  completed: boolean;
  moves: number;
  timeSeconds: number;
  pairs: number;
  isPerfect: boolean; // matched all on first try (moves === pairs)
  score: number;
}

/** Available random images */
export const RANDOM_IMAGES = [
  "airplace.jpg",
  "friendly_blue_monster.jpg",
  "funny_monster.jpg",
  "minion_hero.jpg",
  "minion_muscle.jpg",
  "purple_monther.jpg",
  "r2d2.jpg",
  "robot.jpg",
  "spce.jpg",
  "spiderman.jpg",
  "sweet_monster.jpg",
  "toystory.jpg",
  "yellow_pumpkin.jpg",
  "yellow.jpg",
];

/** Image set info */
export interface ImageSet {
  id: string;
  name: string;
  type: ImageSetType;
  images: string[];
  description?: string;
}

